import { Component } from 'react';
import Head from 'next/head';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import Image from 'next/image';
import { injectIntl } from 'react-intl';
import { withRouter } from 'next/router';
import compose from '@aveneo/frontmoon-utils/lib/hocs/compose';

import { ROUTE_TYPES_VALUES } from 'src/modules/Restaurant';

import Button from 'src/components/Button';
import CallWaiterButton from 'src/components/CallWaiterButton';
import Menu from 'src/components/Menu';
import MenuCategories from 'src/components/Menu/MenuCategories';
import OrderStatus from 'src/components/OrderStatus';
import MenuSelection from 'src/components/Menu/MenuSelection';
import OrderBarPortal from 'src/components/OrderBar/OrderBarPortal';
import QrCodeButton from 'src/components/QrCodeButton';
import LastBillButton from 'src/components/LastBillButton';
import RestaurantDetails from 'src/components/RestaurantDetails';
import MenuCategorySelector from 'src/components/MenuCategorySelector';
import PublishMenuBarPortal from 'src/components/PublishMenuBar/PublishMenuBarPortal';

import theme from 'src/styles/components/RestaurantMenu.module.scss';

class RestaurantMenu extends Component {
    // eslint-disable-next-line react/state-in-constructor
    constructor(props) {
        super(props);

        this.state = {
            isSticky: false
        };
    }

    componentDidMount() {
        const observer = new IntersectionObserver(this.callbackFunction, {
            threshold: 0, rootMargin: '-46px 0px 0px 0px'
        });

        observer.observe(document.getElementById('menuOptions'));

        if (this.props.context?.routeType === ROUTE_TYPES_VALUES.BILL_WAIT) {
            this.showBillNotPaidMessage();
        }

        if (this.props.context?.routeType === ROUTE_TYPES_VALUES.MENU && this.props.code) {
            this.showReadonlyMenuMessage();
        }

        if (this.props.context?.routeType === ROUTE_TYPES_VALUES.RESTAURANT_CLOSED) {
            this.showRestaurantClosedMessage();
        }

        if (this.props.context?.routeType === ROUTE_TYPES_VALUES.AREA_NOT_ACTIVE) {
            this.showAreaNotActiveMessage();
        }
    }

    componentDidUpdate(prevProps) {
        const { context, isInfoRead, code } = this.props;

        if (prevProps.context !== context) {
            const { routeType } = context;

            if (!isInfoRead && routeType === ROUTE_TYPES_VALUES.BILL_WAIT) {
                this.showBillNotPaidMessage();
            }

            if (!isInfoRead && routeType === ROUTE_TYPES_VALUES.MENU && code && code !== prevProps.code) {
                this.showReadonlyMenuMessage();
            }

            if (!isInfoRead && routeType === ROUTE_TYPES_VALUES.RESTAURANT_CLOSED) {
                this.showRestaurantClosedMessage();
            }

            if (!isInfoRead && routeType === ROUTE_TYPES_VALUES.AREA_NOT_ACTIVE) {
                this.showAreaNotActiveMessage();
            }
        }
    }

    callbackFunction = ([entry]) => {
        this.setState((state) => ({ isSticky: entry.intersectionRatio === 0 && !state.isInfoVisible && !state.isSticky }));
    }

    showBillNotPaidMessage = () => {
        const { emitMessage, readInfo, intl } = this.props;

        emitMessage({
            type: 'info',
            title: (
                <>
                    {intl.formatMessage({ id: 'App.billNotPaidTitle' })}
                </>),
            subtitle: (
                <>
                    {intl.formatMessage({ id: 'App.informationSubtitle' })}
                </>),
            message: (
                <div className={theme.popupDescription}>
                    {intl.formatMessage({ id: 'App.billNotPaidDescription' })}
                </div>
            ),
            actions: (
                <div className={theme.actionContainer}>
                    <Button
                      className={theme.action}
                      onClick={this.handleOk}
                      accent
                      raised
                    >
                        {intl.formatMessage({ id: 'App.ok' })}
                    </Button>
                </div>
            ),
            isEscKeyDownEnabled: true,
            overflow: true,
            onDismiss: () => { readInfo(); },
            backgroundIcon: 'icon-cleanup'
        });
    }

    showReadonlyMenuMessage = () => {
        const { emitMessage, readInfo, intl } = this.props;

        emitMessage({
            type: 'info',
            title: (
                <>
                    {intl.formatMessage({ id: 'App.readonlyMenuTitle' })}
                </>),
            subtitle: (
                <>
                    {intl.formatMessage({ id: 'App.informationSubtitle' })}
                </>),
            message: (
                <div className={theme.popupDescription}>
                    {intl.formatMessage({ id: 'App.readonlyMenuDescription' })}
                </div>
            ),
            actions: (
                <div className={theme.actionContainer}>
                    <Button
                      className={theme.action}
                      onClick={this.handleOk}
                      accent
                      raised
                    >
                        {intl.formatMessage({ id: 'App.ok' })}
                    </Button>
                </div>
            ),
            isEscKeyDownEnabled: true,
            overflow: true,
            onDismiss: () => { readInfo(); },
            backgroundIcon: 'icon-info'
        });
    }

    showRestaurantClosedMessage = () => {
        const { emitMessage, readInfo, intl } = this.props;

        emitMessage({
            type: 'info',
            title: (
                <>
                    {intl.formatMessage({ id: 'App.restaurantClosedTitle' })}
                </>),
            subtitle: (
                <>
                    {intl.formatMessage({ id: 'App.informationSubtitle' })}
                </>),
            message: (
                <div className={theme.popupDescription}>
                    {intl.formatMessage({ id: 'App.restaurantClosedDescription' })}
                </div>
            ),
            actions: (
                <div className={theme.actionContainer}>
                    <Button
                      className={theme.action}
                      onClick={this.handleOk}
                      accent
                      raised
                    >
                        {intl.formatMessage({ id: 'App.ok' })}
                    </Button>
                </div>
            ),
            isEscKeyDownEnabled: true,
            overflow: true,
            onDismiss: () => { readInfo(); },
            backgroundIcon: 'icon-clock'
        });
    }

    showAreaNotActiveMessage = () => {
        const { emitMessage, readInfo, intl } = this.props;

        emitMessage({
            type: 'info',
            title: (
                <>
                    {intl.formatMessage({ id: 'App.areaNotActiveTitle' })}
                </>),
            subtitle: (
                <>
                    {intl.formatMessage({ id: 'App.informationSubtitle' })}
                </>),
            message: (
                <div className={theme.popupDescription}>
                    {intl.formatMessage({ id: 'App.areaNotActiveDescription' })}
                </div>
            ),
            actions: (
                <div className={theme.actionContainer}>
                    <Button
                      className={theme.action}
                      onClick={this.handleOk}
                      accent
                      raised
                    >
                        {intl.formatMessage({ id: 'App.ok' })}
                    </Button>
                </div>
            ),
            isEscKeyDownEnabled: true,
            overflow: true,
            onDismiss: () => { readInfo(); },
            backgroundIcon: 'icon-clock'
        });
    }

    handleOk = (e) => {
        e.preventDefault();

        this.props.clearMessages();
    }

    showInfo = (e) => {
        e.preventDefault();

        const homeContainer = document.getElementsByTagName('html')[0];

        if (!this.state.isInfoVisible) {
            homeContainer.classList.add(theme.lockScroll);
            document.body.classList.add(theme.lockScroll);
        } else {
            homeContainer.classList.remove(theme.lockScroll);
            document.body.classList.remove(theme.lockScroll);
        }

        this.setState((state) => ({ isInfoVisible: !state.isInfoVisible }));
    }

    render() {
        const {
            name,
            description,
            isOrderContextDefined,
            backgroundUrl,
            backgroundSrcSetUrls,
            logoUrl,
            logoSrcSetUrls,
            multipleMenus,
            intl,
            context,
            isReadOnlyMenu,
            router
        } = this.props;
        const { isSticky, isInfoVisible } = this.state;

        return (
            <div className={classnames(theme.container, { [theme.isSticky]: isSticky })}>
                <Head>
                    {name && (
                        <meta
                          property="og:title"
                          content={`${intl.formatMessage({ id: `App.restaurant` })} | ${name}`}
                          key="ogtitle"
                        />
                    )}
                    {description && <meta property="og:description" content={description} key="ogdesc" />}
                    {backgroundUrl && <meta property="og:image" content={backgroundUrl} />}
                    {backgroundUrl && <link rel="image_src" href={backgroundUrl} />}
                </Head>
                <div className={classnames(theme.header, { [theme.infoVisible]: isInfoVisible })}>
                    <div className={theme.headerDescription}>
                        {isReadOnlyMenu && (
                            <span className={classnames('icon-menu', theme.headerIcon)} />
                        )}
                        {!isReadOnlyMenu && (
                            <span className={classnames('icon-serving', theme.headerIcon)} />
                        )}
                        <div>
                            <div className={theme.headerTitle}>{context?.restaurantName}</div>
                            {isReadOnlyMenu && (
                                <div className={theme.headerSubTitle}>
                                    {intl.formatMessage({ id: 'App.readonlyMenuTitle' })}
                                </div>
                            )}
                            {!isReadOnlyMenu && (
                                <div className={theme.headerSubTitle}>
                                    {intl.formatMessage(
                                        { id: 'App.tableDescription' },
                                        { tableName: context?.tableName, tableNumber: context?.tableNumber }
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                    <div>
                        <QrCodeButton size="sm" flat />
                        <CallWaiterButton isIconButton flat />
                    </div>
                </div>
                <div className={classnames(theme.logoTileContainer, { [theme.isSticky]: isSticky })}>
                    <div className={classnames(theme.logoTile, { [theme.infoVisible]: isInfoVisible })} id="menuOptions">
                        {backgroundUrl && (
                            <Image
                              className={theme.logoBackground}
                              alt={intl.formatMessage({ id: 'App.restaurantBackgroundAlt' })}
                              src={backgroundUrl}
                              srcset={backgroundSrcSetUrls}
                              layout="fill"
                              objectFit="cover"
                              unoptimized="true"
                            />
                            )}
                        <div className={theme.details}>
                            <RestaurantDetails />
                        </div>
                        <div>
                            <Button
                              className={classnames(theme.moreInfoButton, { [theme.infoVisible]: isInfoVisible })}
                              floating
                              mini
                              icon="icon-info"
                              aria-label="more info"
                              onClick={this.showInfo}
                            />
                            <Button
                              className={classnames(theme.closeInfoButton, { [theme.infoVisible]: isInfoVisible })}
                              floating
                              mini
                              name="close"
                              icon="icon-close"
                              onClick={this.showInfo}
                            />
                        </div>
                        <div className={theme.logoContainer}>
                            {logoUrl && (
                            <Image
                              src={logoUrl}
                              srcset={logoSrcSetUrls}
                              width={200}
                              height={200}
                              alt={name}
                              unoptimized="true"
                            />
                            )}
                        </div>
                    </div>
                    <div className={theme.spacing} />
                    <div className={classnames(theme.menuOptions, { [theme.isSticky]: isSticky })}>
                        {multipleMenus && (
                            <MenuSelection flat={isSticky} />
                        )}
                        <MenuCategories />
                    </div>
                </div>
                <div
                  className={classnames(theme.content, {
                      [theme.withOrderBar]: isOrderContextDefined && !isReadOnlyMenu && !isInfoVisible
                  })}
                >
                    <MenuCategorySelector />
                    <Menu />
                </div>
                <LastBillButton />
                {(isOrderContextDefined && !isReadOnlyMenu && !isInfoVisible) && (
                    <OrderBarPortal OrderBarComponent={OrderStatus} />
                )}
                {(!isOrderContextDefined && router?.query?.token) && (
                    <PublishMenuBarPortal token={router.query.token} />
                )}
            </div>
        );
    }
}

RestaurantMenu.propTypes = {
    name: PropTypes.string,
    description: PropTypes.string,
    backgroundUrl: PropTypes.string.isRequired,
    backgroundSrcSetUrls: PropTypes.string.isRequired,
    logoUrl: PropTypes.string.isRequired,
    logoSrcSetUrls: PropTypes.string.isRequired,
    isOrderContextDefined: PropTypes.bool.isRequired,
    multipleMenus: PropTypes.bool,
    context: PropTypes.shape({
        tableId: PropTypes.string,
        restaurantId: PropTypes.string,
        restaurantName: PropTypes.string,
        tableName: PropTypes.string,
        tableNumber: PropTypes.number,
        routeType: PropTypes.number
    }),
    emitMessage: PropTypes.func.isRequired,
    readInfo: PropTypes.func.isRequired,
    clearMessages: PropTypes.func.isRequired,
    intl: PropTypes.object.isRequired,
    isInfoRead: PropTypes.bool.isRequired,
    isReadOnlyMenu: PropTypes.bool.isRequired,
    code: PropTypes.string,
    router: PropTypes.object.isRequired
};

export default compose(
    withRouter,
    injectIntl
)(RestaurantMenu);
